'use client';

import type {Document} from '@contentful/rich-text-types';
import {Button as BaseButton} from '@mui/base/Button';
import {Modal as BaseModal} from '@mui/base/Modal';
import {
  Dialog as RMWCDialog,
  type DialogProps as RMWCDialogProps,
} from '@rmwc/dialog';
import clsx from 'clsx';
import {
  type JSX,
  type SyntheticEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {ComponentProps, FC, PropsWithChildren, ReactNode} from 'react';
import {RichDiv} from '../../rich-text';
import styles from './Dialog.module.scss';
import '@rmwc/dialog/styles';
import {CircularProgress} from '@rmwc/circular-progress';

import {
  DialogBackdrop,
  Dialog as DialogHeadlessUI,
  DialogPanel,
} from '@headlessui/react';

const Backdrop = forwardRef<
  HTMLDivElement,
  {open?: boolean; className: string}
>((props, ref) => {
  const {open, className, ...other} = props;
  return (
    <div
      className={clsx(
        {'base-Backdrop-open': open},
        className,
        styles.styledBackdrop,
      )}
      ref={ref}
      {...other}
    />
  );
});

interface ActionButton {
  label: string;
  buttonHandler: (event: SyntheticEvent | Event) => void;
  className?: string;
}

export interface DialogCardProps {
  open: boolean;
  handleClose: (event: SyntheticEvent | Event) => void;
  title: string;
  isRichText: boolean;
  description?: string;
  richText?: Document | unknown;
  actionButtons?: Array<ActionButton>;
  subTitle?: string;
}

/*
 * If no title is passed in then display circular progress.
 * Add additional flag field or create a seperate dialog component
 * if there will be more complicate use cases.
 */
export const DialogCard = ({
  open,
  handleClose,
  title,
  subTitle,
  actionButtons,
  isRichText,
  description,
  richText,
}: DialogCardProps): JSX.Element => {
  return (
    <BaseModal
      data-testid="base-modalbox"
      className={styles.dialog}
      open={open}
      onClose={handleClose}
      slots={{backdrop: Backdrop}}
    >
      <div className={styles.dialogContent}>
        <div className={styles.contentText}>
          {title ? (
            <>
              <div
                data-testid="base-modalbox-title"
                className={styles.dialogTitle}
              >
                {title}
              </div>
              <div className={styles.dialogHeadingUnderline} />
            </>
          ) : (
            <CircularProgress
              className={styles.circularProgress}
              color="primary"
            />
          )}
          {subTitle && (
            <div
              data-testid="base-modalbox-subtitle"
              className={styles.dialogSubTitle}
            >
              {subTitle}
            </div>
          )}
          {!isRichText && description && (
            <div
              data-testid="base-modalbox-description"
              className={styles.dialogDescription}
            >
              {description}
            </div>
          )}
          {isRichText && (
            <div
              data-testid="base-modalbox-richText"
              className={styles.dialogDescription}
            >
              <RichDiv>{richText as Document}</RichDiv>
            </div>
          )}
        </div>

        {actionButtons && (
          <div
            data-testid="base-modalbox-actionbar"
            className={styles.dialogActionBar}
          >
            {actionButtons?.map(
              (
                {label, className, buttonHandler}: ActionButton,
                index: number,
              ) => (
                <BaseButton
                  key={`dialog-action-btn-${index}-${label}`}
                  onClick={buttonHandler}
                  className={clsx(styles.actionButton, className)}
                >
                  {label}
                </BaseButton>
              ),
            )}
          </div>
        )}
      </div>
    </BaseModal>
  );
};

/**
 * **Warning:** The current version of the RMWC Dialog component (v14) is incompatible with React 18 in strict mode due to known issues.
 *
 *  Refer to the following GitHub issue for more information and potential workarounds: https://github.com/rmwc/rmwc/issues/815
 *
 * We recommend using a different dialog component that is compatible with React 18 for your application.
 *
 * @see {@link https://github.com/rmwc/rmwc/issues/815}
 */
export const DialogR = (
  props: Partial<RMWCDialogProps & PropsWithChildren & {className: string}>,
): React.JSX.Element => {
  const {className, children, ...restProps} = props;
  return (
    <div className={styles.dialogContainer}>
      <RMWCDialog {...restProps}>
        <div className={className}>{children}</div>
      </RMWCDialog>
    </div>
  );
};

export {DialogActions} from './DialogActions/DialogActions';
export {DialogContent} from './DialogContent/DialogContent';
export {DialogContentText} from './DialogContentText/DialogContentText';
export {DialogTitle} from './DialogTitle/DialogTitle';

export type DialogProps = ComponentProps<typeof DialogHeadlessUI>;
export interface DialogInterface extends DialogProps {
  isDisabledBackdrop?: boolean;
}
/**
 * A reusable Dialog component based on Headless UI's Dialog.
 *
 * @param {DialogProps} props - Component props.
 * @returns {JSX.Element} The Dialog component.
 *
 * @see {@link https://headlessui.com/react/dialog#dialog}
 */
export const Dialog: FC<DialogInterface> = ({
  open,
  onClose,
  children,
  className,
  isDisabledBackdrop,
  ...props
}: DialogInterface): ReactNode => {
  const [isOpen, setIsOpen] = useState(open);
  const backdropRef = useRef<HTMLDivElement>(null);
  const onClickOutside = useCallback(
    (value: boolean) => {
      if (
        isDisabledBackdrop &&
        backdropRef.current &&
        !backdropRef.current.contains(document.activeElement)
      ) {
        return;
      }
      onClose(value);
      setIsOpen(false);
    },
    [onClose, isDisabledBackdrop],
  );

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  return (
    <DialogHeadlessUI
      open={isOpen}
      onClose={onClickOutside}
      {...props}
    >
      <DialogBackdrop
        className={clsx(styles.backdrop, styles.animateFade)}
        transition
        data-testid="dialog-backdrop"
        ref={backdropRef}
      />
      <DialogPanel
        className={clsx(styles.dialogPanel, styles.animateFade, className)}
        transition
      >
        {children}
      </DialogPanel>
    </DialogHeadlessUI>
  );
};
